import React, { useEffect, useState } from 'react';
import { message, Spin } from 'antd';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import smallLogo from '../../../small_deepecho_icon.png';
import darksmallLogo from '../../../small_deepecho_icon_dark.png';
import {
  useVerifyAccountSetup,
  useResendVerification,
} from '../../../helpers/api/users/users';
import { apiClient } from '../../../helpers/apiHelper';
import { PhoneInput } from 'react-international-phone';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import { validatePasswordStrength } from '../../../helpers/passwordValidation';
import { FormField } from '../../ComponnentsUI/common/FormField';
import { Input } from '../../ComponnentsUI/common/Input';
import { Button } from '../../ComponnentsUI/common/Button';

const SetUpAccount = () => {
  const { t } = useTranslation('SetUpAccount');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [userId, setUserId] = useState('');
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone_number: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    phone_number: '',
    password: '',
    confirmPassword: '',
  });
  const token = searchParams.get('token');
  const backend_url =
    (window as any).RUNTIME_ENV?.BACKEND_URL ??
    process.env.BACKEND_URL ??
    'http://localhost:8000/api/v1';

  const onSuccess = (response) => {
    if (response) {
      console.log('account setup response', response);
      // message.success('Account setup successfully');
      navigate('/login', {
        state: {
          message: t('successMessage'),
        },
      });
    }
  };

  const onError = (response, error) => {
    console.log('error:', error, response);
    // message.error('An error occurred while setting up your account.');

    if (response?.data?.detail && Array.isArray(response.data.detail)) {
      handleServerValidationErrors(response.data.detail);
    } else if (response?.detail && Array.isArray(response.detail)) {
      handleServerValidationErrors(response.detail);
    } else {
      const errorMessage =
        error.message || 'An error occurred during account setup';
      setError(errorMessage);

      // Check if token is expired
      if (
        errorMessage.toLowerCase().includes('expired') ||
        errorMessage.toLowerCase().includes('invalid token')
      ) {
        setIsTokenExpired(true);

        // Try to extract user ID from token if possible
        try {
          // This is a simplified example - you might need to adjust based on your token structure
          const tokenParts = token?.split('.') || [];
          if (tokenParts.length > 1) {
            const payload = JSON.parse(atob(tokenParts[1]));
            if (payload.user_id) {
              setUserId(payload.user_id);
            }
          }
        } catch (e) {
          console.error('Failed to extract user ID from token', e);
        }
      }
    }
  };

  const resendVerification = useResendVerification(
    (response, data) => {
      message.success(t('resendSuccessMessage'));
    },
    (response, error) => {
      message.error(t('resendErrorMessage'));
    }
  );

  const verifySetup = useVerifyAccountSetup(onSuccess, onError);

  if (!token) {
    return (
      <div className="min-h-screen w-full flex justify-center items-center p-4 bg-card-color-200 dark:bg-gray-900">
        <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-lg bg-card-color-100 dark:bg-gray-800 text-center">
          <h1 className="text-xl font-bold text-red-600 dark:text-red-400">{t('invalidSetupTitle')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('invalidSetupMessage')}
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) return;

    verifySetup.mutate({
      token,
      password: formData.password,
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone_number: formData.phone_number,
    });
  };

  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setFormData((prev) => ({ ...prev, [name]: value }));

  //   if (errors[name]) {
  //     setErrors((prev) => ({ ...prev, [name]: '' }));
  //   }
  // };
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };

    setFormData(updatedFormData);

    const newErrors = { ...errors };

    // Clear the error of the changed field
    if (newErrors[name]) {
      newErrors[name] = '';
    }

    // Real-time confirm password match check
    if (name === 'password' || name === 'confirmPassword') {
      if (
        updatedFormData.confirmPassword &&
        updatedFormData.password !== updatedFormData.confirmPassword
      ) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        newErrors.confirmPassword = '';
      }
    }

    setErrors(newErrors);
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...errors };

    // Validate first name
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      isValid = false;
    }

    // Validate last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      isValid = false;
    }

    // Validate password
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else {
      const passwordError = validatePasswordStrength(formData.password);
      if (passwordError) {
        newErrors.password = passwordError;
        isValid = false;
      }
    }

    // Validate confirm password
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    // Validate phone number if provided
    if (formData.phone_number) {
      try {
        const phoneNumber = parsePhoneNumberFromString(formData.phone_number);
        if (!phoneNumber || !phoneNumber.isValid()) {
          newErrors.phone_number = 'Invalid phone number';
          isValid = false;
        }
      } catch (e) {
        newErrors.phone_number = 'Invalid phone number';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleServerValidationErrors = (errorDetails) => {
    const newErrors = { ...errors };
    console.log('Server validation errors:', errorDetails);

    errorDetails.forEach((error) => {
      // Extract field name from the location path
      const fieldPath = error.loc;
      if (fieldPath && fieldPath.length >= 2) {
        const fieldName = fieldPath[1];

        // Map backend field names to form field names if needed
        let formFieldName = fieldName;
        if (fieldName === 'first_name') formFieldName = 'firstName';
        if (fieldName === 'last_name') formFieldName = 'lastName';

        // Set the error message for this field
        if (newErrors.hasOwnProperty(formFieldName)) {
          newErrors[formFieldName] = error.msg;
        }
      }
    });

    setErrors(newErrors);
  };

  const handleGoogleSetup = () => {
    window.location.href = backend_url + '/auth/google/login?mode=setup';
  };
  const handleSSOSetup = () => {
    window.location.href = backend_url + '/saml/login';
  };

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const response = await apiClient.get('/settings/features-enabled');
        setSsoEnabled(response.data?.sso_enabled || false);
      } catch (fetchError) {
        console.error('Error fetching features:', fetchError);
      }
    };
    fetchFeatures();
  }, [backend_url]);
  useDocumentTitle(`DEEPECHO - ${t('pageTitle')}`);

  return (
    <div className="min-h-screen w-full flex justify-center items-center p-4 bg-card-color-200 dark:bg-gray-900">
      <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-lg bg-card-color-100 dark:bg-gray-800">
        {/* Logo and App Name Section */}
        <div className="flex items-center justify-center mb-6 space-x-3">
          <img
            src={smallLogo}
            className="dark:hidden transition-all duration-300 h-10"
            alt="Deep Echo Logo"
          />
          <img
            src={darksmallLogo}
            className="hidden dark:block transition-all duration-300 h-10"
            alt="Deep Echo Logo"
          />
          <div>
            <h1 className="text-xl font-bold text-title-page-color-100">DEEPECHO</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('subtitle') || 'Medical Imaging Platform'}</p>
          </div>
        </div>
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-title-page-color-100">
            {t('pageTitle')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('pageSubtitle')}
          </p>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-600 dark:text-red-400 mb-6">
            {error}
          </div>
        )}

        {isTokenExpired && userId && (
          <div className="text-center mb-6 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20">
            <p className="text-yellow-700 dark:text-yellow-400 mb-2">
              {t('tokenExpiredMessage')}
            </p>
            <button
              onClick={() => resendVerification.mutate(userId)}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
              disabled={resendVerification.isPending}
            >
              {resendVerification.isPending ? (
                <Spin size="small" />
              ) : (
                t('resendVerificationButton')
              )}
            </button>
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-5">
          <FormField
            label={t('firstNameLabel')}
            htmlFor="firstName"
            error={errors.firstName}
          >
            <Input
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder={t('firstNamePlaceholder')}
              className={`bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-600 ${errors.firstName ? 'border-red-500 dark:border-red-400' : ''
                }`}
            />
          </FormField>

          <FormField
            label={t('lastNameLabel')}
            htmlFor="lastName"
            error={errors.lastName}
          >
            <Input
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder={t('lastNamePlaceholder')}
              className={`bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-600 ${errors.lastName ? 'border-red-500 dark:border-red-400' : ''
                }`}
            />
          </FormField>

          <FormField
            label={t('phoneNumberLabel')}
            htmlFor="phone_number"
            error={errors.phone_number}
          >
            <PhoneInput
              defaultCountry="ma"
              countrySelectorStyleProps={{ buttonStyle: { backgroundColor: 'transparent', borderColor: 'rgb(203 213 225)', color: 'inherit' } }}
              value={formData.phone_number}
              onChange={(value) => {
                setFormData((prev) => ({ ...prev, phone_number: value }));
                if (errors.phone_number) {
                  setErrors((prev) => ({ ...prev, phone_number: '' }));
                }
              }}
              inputClassName={` w-full !bg-transparent dark:!bg-gray-800 !text-title-page-color-300 ${errors.phone_number ? 'rip-input--error' : ''}`}
            />
          </FormField>

          <FormField
            label={t('passwordLabel')}
            htmlFor="password"
            error={errors.password}
          >
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('passwordPlaceholder')}
              className={`bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-600 ${errors.password ? 'border-red-500 dark:border-red-400' : ''
                }`}
            />
          </FormField>

          <FormField
            label={t('confirmPasswordLabel')}
            htmlFor="confirmPassword"
            error={errors.confirmPassword}
          >
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('confirmPasswordPlaceholder')}
              className={`bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-600 ${errors.confirmPassword ? 'border-red-500 dark:border-red-400' : ''
                }`}
            />
          </FormField>

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={verifySetup.isPending || isTokenExpired}
            >
              {verifySetup.isPending ? t('settingUp') : t('completeSetup')}
            </Button>
          </div>
        </form>
        <div className="mt-4 space-y-3">
          {/* Google OAuth button - always available */}
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={handleGoogleSetup}
          >
            Continue with Google
          </Button>

          {/* SSO/Workspace button - only shown when SSO is enabled */}
          {ssoEnabled && (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={handleSSOSetup}
            >
              Sign in with Workspace SSO
            </Button>
          )}
        </div>
        <p className="text-sm text-center text-gray-500 dark:text-gray-400 mt-6">
          {t('alreadyHaveAccount')}{' '}
          <Link
            to="/login"
            className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
          >
            {t('backToSignIn')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default SetUpAccount;
